import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"
import { nanoid } from "nanoid"
import { ErrorContextValue, ErrorLogInput, ErrorRecord } from "../types/errors"

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined)

const normalizeError = (error: unknown) => {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack }
  }
  if (typeof error === "string") {
    return { message: error }
  }
  try {
    return { message: JSON.stringify(error) }
  } catch {
    return { message: "Unknown error" }
  }
}

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrorsState] = useState<ErrorRecord[]>([])
  const errorsRef = useRef<ErrorRecord[]>([])
  const setErrors = useCallback(
    (update: ErrorRecord[] | ((prev: ErrorRecord[]) => ErrorRecord[])) => {
      errorsRef.current = typeof update === "function" ? update(errorsRef.current) : update
      setErrorsState(errorsRef.current)
    },
    [],
  )

  const logError = useCallback((input: ErrorLogInput) => {
    const { message, stack } = normalizeError(input.error)
    const existing = errorsRef.current.find(
      (error) =>
        !error.resolvedAt &&
        error.component === input.component &&
        error.operation === (input.operation || input.metadata?.action) &&
        error.message === message &&
        error.userMessage === input.userMessage,
    )
    // Repeated identical failures (e.g. a retry loop) must not stack identical panels.
    if (existing) {
      return existing
    }
    const record: ErrorRecord = {
      id: nanoid(),
      message,
      userMessage: input.userMessage,
      component: input.component,
      operation: input.operation || input.metadata?.action,
      severity: input.severity || "error",
      timestamp: Date.now(),
      stack,
      context: input.context || (input.metadata ? { metadata: input.metadata } : undefined),
    }

    setErrors((prev) => [...prev, record])

    const label = record.component ? `[${record.component}]` : "[app]"
    const logger = record.severity === "error" ? console.error : console.warn
    logger(`${label} ${record.message}`, {
      operation: record.operation,
      context: record.context,
      stack: record.stack,
    })

    return record
  }, [setErrors])

  const clearError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((error) => error.id !== id))
  }, [setErrors])

  const resolveError = useCallback((id: string) => {
    setErrors((prev) =>
      prev.map((error) => (error.id === id ? { ...error, resolvedAt: Date.now() } : error)),
    )
  }, [setErrors])

  const clearByComponent = useCallback((component?: string) => {
    setErrors((prev) =>
      component ? prev.filter((error) => error.component !== component) : [],
    )
  }, [setErrors])

  const getErrorsByComponent = useCallback(
    (component?: string) =>
      component ? errors.filter((error) => error.component === component) : errors,
    [errors],
  )

  const value = useMemo<ErrorContextValue>(
    () => ({
      errors,
      logError,
      clearError,
      clearByComponent,
      resolveError,
      getErrorsByComponent,
    }),
    [clearByComponent, clearError, errors, getErrorsByComponent, logError, resolveError],
  )

  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
}

export const useErrorHandlingContext = () => {
  const ctx = useContext(ErrorContext)
  if (!ctx) {
    throw new Error("useErrorHandlingContext must be used within an ErrorProvider")
  }
  return ctx
}
